/**
 * Domínio do gráfico de instrumentos (M6) — transformação de leituras em série,
 * cálculo de tendência e linhas de referência. TS puro, testável sem mock.
 */
import type { Canal, Instrument, InstrumentType, Reading } from '@/domain';
import { ehMarco } from '@/domain/instrumentos/linhas-de-marco';
import { ehBilateral, type FaixaCanal } from '@/domain/instrumentos/tilt';
import { filterInstrumentsByDam, type DamSelection } from '@/domain/instrumentos/dam-filter';

export interface ChartPoint {
  /** Rótulo curto do eixo X (dd/MM). */
  date: string;
  value: number;
  /** Timestamp (ms) para ordenação estável. */
  ts: number;
}

export type Trend = 'crescente' | 'decrescente' | 'estavel';

export interface ThresholdLine {
  label: string;
  value: number;
  color: string;
}

/**
 * Unidade de medida por tipo de instrumento.
 *
 * Marco superficial (MS/MSE) em METROS: o gráfico segue o canal `vertical`, que é a
 * cota absoluta da campanha (~803 m) — ver `canalDaSerie`. O deslocamento em mm
 * vive na tabela de marcos do Resumo Técnico, não aqui.
 */
export const TYPE_UNIT: Record<string, string> = {
  MS: 'm', MSE: 'm', INA: 'm', PZ: 'kPa', TILT: '°', PLV: 'mm', MV: 'L/s', RL: 'm',
};

/**
 * Canal que a curva principal do gráfico segue — um por curva.
 *
 * Instrumento multi-canal sem filtro traz, com `limit 60`, os canais embaralhados
 * numa curva só: no tiltímetro a temperatura esmagava os eixos (GUPM-270); no marco
 * (GUPM-329) o northing (~7.966.000 m) esmagava o easting e a cota. `null` =
 * instrumento de canal único, sem filtro.
 */
export function canalDaSerie(type: string): Canal | null {
  if (type === 'TILT') return 'eixo_a';
  if (ehMarco(type)) return 'vertical';
  return null;
}

/**
 * O instrumento tem gráfico de série no M6? Marco superficial (MS/MSE) não: campanha
 * mensal com variação milimétrica não se lê como série — decisão do dono em 28/09/2026.
 * O deslocamento do marco vive na tabela de Marcos Superficiais (Dashboard e Mapa).
 */
export function temGraficoDeSerie(type: string): boolean {
  return !ehMarco(type);
}

/**
 * "Valor atual" de instrumento sem faixa por canal: a leitura mais recente DO
 * CANAL que o gráfico segue. `readings_current` traz uma linha por canal; pegar a
 * mais recente de todas fazia o marco exibir o easting (~296.000 m) no cabeçalho
 * de um gráfico da cota.
 */
export function leituraAtualDaSerie(leituras: Reading[], type: string): Reading | undefined {
  const canal = canalDaSerie(type);
  const doCanal = canal ? leituras.filter((r) => r.canal === canal) : leituras;
  return [...doCanal].sort((a, b) => b.ts.getTime() - a.ts.getTime())[0];
}

// Delta mínimo (última − penúltima leitura) para classificar tendência como
// alta/baixa em vez de estável. Valor agnóstico ao tipo de instrumento —
// se precisar de sensibilidade por unidade (kPa vs m vs mm), parametrizar por TYPE_UNIT.
const TREND_THRESHOLD = 0.5;

function ddMM(d: Date): string {
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}`;
}

/** Monta a série do gráfico a partir das leituras, em ordem cronológica. */
export function toChartSeries(readings: Reading[]): ChartPoint[] {
  return readings
    .map((r) => ({ date: ddMM(r.ts), value: r.value, ts: r.ts.getTime() }))
    .sort((a, b) => a.ts - b.ts);
}

/** Tendência da janela final da série (Δ > 0,5 crescente; < -0,5 decrescente). */
export function computeTrend(series: ChartPoint[], window = 5): Trend {
  if (series.length < 2) return 'estavel';
  const tail = series.slice(-window);
  const diff = tail[tail.length - 1]!.value - tail[0]!.value;
  if (diff > TREND_THRESHOLD) return 'crescente';
  if (diff < -TREND_THRESHOLD) return 'decrescente';
  return 'estavel';
}

/** Linhas de threshold (uma por limite não-nulo), com a cor do nível. */
export function thresholdLines(instrument: Instrument): ThresholdLine[] {
  const lines: ThresholdLine[] = [];
  if (instrument.levelAtencaoMax != null) {
    lines.push({ label: 'Atenção', value: instrument.levelAtencaoMax, color: '#eab308' });
  }
  if (instrument.levelAlertaMax != null) {
    lines.push({ label: 'Alerta', value: instrument.levelAlertaMax, color: '#f97316' });
  }
  if (instrument.levelEmergenciaMax != null) {
    lines.push({ label: 'Emergência', value: instrument.levelEmergenciaMax, color: '#ef4444' });
  }
  return lines;
}

// ---------------------------------------------------------------------------
// GUPM-211 — tipos monitorados, filtros e linhas de referência do gráfico
// ---------------------------------------------------------------------------

/**
 * Tipos exibidos no M6: indicador de NA, piezômetro, marcos superficiais e
 * tiltímetro. Estação meteorológica (EM), pluviômetro, régua e vazão seguem como
 * *fonte* de overlays no gráfico, mas não são selecionáveis aqui.
 *
 * GUPM-270 — TILT entrou. Ficara de fora no GUPM-211 porque o tiltímetro só
 * gravava a resultante (número sem significado físico) e não tinha faixa nenhuma:
 * não havia o que plotar nem contra o que comparar. Com os eixos e a carta por
 * canal, passa a ser o instrumento com a série mais densa do app (30 em 30 min).
 */
export const MONITORED_TYPES: readonly InstrumentType[] = ['INA', 'PZ', 'MS', 'MSE', 'TILT'];

/**
 * Mantém só os instrumentos dos tipos monitorados E **ativos** (descarta PLV/MV/EM/…).
 *
 * GUPM-270 — o gate de `isActive` entrou porque o seletor listava os 26 retirados
 * como chips escolhíveis: PZC-01 a 05, PE-1 a 4, `TIL-01_retirado`, e pares de código
 * repetido (INA-03 ativo ao lado de INA-03 retirado, indistinguíveis no chip).
 * Nenhum deles tem carta ou coordenada — clicar num só rendia gráfico sem régua de um
 * instrumento que saiu do monitoramento. Retirado tem histórico preservado no banco,
 * não presença no seletor.
 */
export function filterMonitoredInstruments(instruments: Instrument[]): Instrument[] {
  return instruments.filter((i) => i.isActive && MONITORED_TYPES.includes(i.type));
}

/** Rótulo do tipo no filtro — MSE dobra em MS, como no protótipo. */
export function typeFilterLabel(type: InstrumentType): string {
  return type === 'MSE' ? 'MS' : type;
}

/**
 * Opções do select "Tipo", DERIVADAS do catálogo de tipos monitorados.
 *
 * GUPM-270 — era uma lista literal no `FilterBar` (`['INA','PZ','MS']`), segunda
 * fonte de verdade ao lado de `MONITORED_TYPES`: somar TILT ao catálogo deixou o
 * filtro para trás, com tiltímetro selecionável no chip e ausente do select.
 */
export const TIPO_OPTS: readonly string[] = [...new Set(MONITORED_TYPES.map(typeFilterLabel))];

/** Recorte da barra de filtros do M6 (estrutura + tipo + seção), fiel ao V8. */
export interface InstrumentFilters {
  /** `damId` (uuid) ou `'all'`. */
  dam: DamSelection;
  /** `''` (todos) | rótulo do filtro de tipo (`'INA' | 'PZ' | 'MS'`; MS cobre MSE). */
  tipo: string;
  /** `''` (todas) | seção (ex.: `"A-A'"`). */
  sec: string;
}

/**
 * Aplica os três recortes da barra de filtros. Combináveis; recorte vazio (`''`
 * / `'all'`) não filtra aquela dimensão.
 */
export function applyInstrumentFilters(
  instruments: Instrument[],
  f: InstrumentFilters,
): Instrument[] {
  let out = filterInstrumentsByDam(instruments, f.dam);
  if (f.tipo) out = out.filter((i) => typeFilterLabel(i.type) === f.tipo);
  if (f.sec) out = out.filter((i) => (i.section || '—') === f.sec);
  return out;
}

/**
 * Linha de referência do gráfico — threshold (nível de controle) ou cota.
 * O estilo do traço (tracejado × sólido) é derivado de `kind` na camada de
 * apresentação — o domínio não conhece detalhes de SVG/Recharts.
 */
export interface ReferenceLineSpec {
  label: string;
  value: number;
  color: string;
  kind: 'threshold' | 'cota';
}

/** Cor neutra das linhas de cota de boca/fundo (visível no tema escuro). */
const COTA_COLOR = '#cbd5e1';

/**
 * Linhas de referência do gráfico de um instrumento: Níveis de Controle
 * (Atenção/Alerta/Emergência, tracejadas) + Cota de boca/fundo (sólidas, só
 * INA/PZ — marcos medem deslocamento em mm, não cota). Limites nulos são omitidos.
 */
export function buildReferenceLines(
  instrument: Instrument,
  faixaCanal?: FaixaCanal | null,
): ReferenceLineSpec[] {
  const lines: ReferenceLineSpec[] = [];

  // GUPM-270 — canal bilateral (tiltímetro): a régua é em MÓDULO, então cada nível
  // vira DUAS linhas, uma em cada sentido. Vem da carta vigente do canal, não dos
  // campos legados `level*Max` do instrumento — que para TILT são nulos, e o
  // gráfico sairia sem régua nenhuma mesmo com a carta carregada por eixo.
  if (faixaCanal) {
    const niveis = [
      { label: 'Atenção', modulo: faixaCanal.inicioAtencao, color: '#eab308' },
      { label: 'Alerta', modulo: faixaCanal.inicioAlerta, color: '#f97316' },
      { label: 'Emergência', modulo: faixaCanal.inicioEmergencia, color: '#ef4444' },
    ];
    for (const n of niveis) {
      lines.push({ label: `${n.label} (+)`, value: n.modulo, color: n.color, kind: 'threshold' });
      lines.push({ label: `${n.label} (−)`, value: -n.modulo, color: n.color, kind: 'threshold' });
    }
    return lines;
  }

  // GUPM-270 — bilateral SEM faixa por canal fica sem régua nenhuma, em vez de cair
  // nos `level_*_max` escalares. Acontece antes da carga da carta e na janela em que
  // a query de faixas ainda está em voo; o resíduo de seed do TIL-02 da Barragem B
  // (1/2/3 °, em produção) desenharia ali uma régua unilateral e 2,5× menor que a
  // real, com uma leitura de 2,5° aparecendo acima de "Atenção". Cego, não régua
  // errada — ADR-0027.
  if (ehBilateral(instrument.type)) return lines;

  // Níveis de Controle — mesma ordem/cores de `thresholdLines`.
  for (const t of thresholdLines(instrument)) {
    lines.push({ ...t, kind: 'threshold' });
  }

  // Cota de boca (topo) e de fundo — só para INA/PZ (leitura em metros/cota).
  const isCotaType = instrument.type === 'INA' || instrument.type === 'PZ';
  if (isCotaType) {
    if (instrument.cotaTopo != null) {
      lines.push({ label: 'Cota de boca', value: instrument.cotaTopo, color: COTA_COLOR, kind: 'cota' });
    }
    if (instrument.cotaFundo != null) {
      lines.push({ label: 'Cota de fundo', value: instrument.cotaFundo, color: COTA_COLOR, kind: 'cota' });
    }
  }

  return lines;
}
